import { Box, Container, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CreditCardOff as CreditCardOffIcon } from "@mui/icons-material";
import { useCompanySubscription } from "../../hooks/billing.hooks";
import { useCompanyInfo } from "../../hooks/company.hooks.ts";
import LoadingBackdrop from "../loading-backdrop";
import {
  borderColor,
  secondaryTextColor,
  textColor,
  whiteColor,
} from "../../scss/colors.module.scss";
import {
  customFontSize16,
  customFontSizeLg,
  customFontSizeSubtitle,
  fontBold,
  fontRegular,
  fontSemiBold,
} from "../../scss/fonts.module.scss";

const PREFIX = "SubscriptionGate";

const classes = {
  container: `${PREFIX}-container`,
  paper: `${PREFIX}-paper`,
  illustration: `${PREFIX}-illustration`,
  illustrationIcon: `${PREFIX}-illustrationIcon`,
  title: `${PREFIX}-title`,
  subtitle: `${PREFIX}-subtitle`,
  helpText: `${PREFIX}-helpText`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: whiteColor,
    padding: theme.spacing(2),
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(6),
    textAlign: "center",
    borderRadius: "20px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
    backgroundColor: whiteColor,
    border: `1px solid ${borderColor}`,
    maxWidth: "600px",
    width: "100%",
  },

  [`& .${classes.illustration}`]: {
    width: "120px",
    height: "120px",
    margin: "0 auto 2rem",
    borderRadius: "50%",
    background: "var(--primaryColor10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "3px solid var(--primaryColorTint)",
  },

  [`& .${classes.illustrationIcon}`]: {
    fontSize: "3.5rem",
    color: "var(--primaryColor)",
  },

  [`& .${classes.title}`]: {
    fontSize: customFontSizeSubtitle,
    fontWeight: 700,
    fontFamily: fontBold,
    color: textColor,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.subtitle}`]: {
    fontSize: customFontSizeLg,
    fontFamily: fontRegular,
    color: secondaryTextColor,
    marginBottom: theme.spacing(4),
    lineHeight: 1.6,
  },

  [`& .${classes.helpText}`]: {
    fontFamily: fontSemiBold,
    fontSize: customFontSize16,
    color: "var(--primaryColor)",
  },
}));

const SubscriptionGate = ({ children }: { children: React.ReactNode }) => {
  const { data: companyInfo, isLoading: isCompanyInfoLoading } =
    useCompanyInfo();
  const {
    data: subscription,
    isLoading: isSubscriptionLoading,
    isFetched: isSubscriptionFetched,
  } = useCompanySubscription(companyInfo?.id) as any;

  // Wait for subscription data to load
  if (
    (isSubscriptionLoading && !isSubscriptionFetched) ||
    isCompanyInfoLoading
  ) {
    return <LoadingBackdrop />;
  }
  // If there's an active subscription, render children
  if (subscription?.lago_id || !companyInfo?.domain) {
    return children;
  }

  // No active subscription — show blocking screen
  return (
    <StyledBox className={classes.container}>
      <Container
        maxWidth="md"
        sx={{ display: "flex", justifyContent: "center" }}
      >
        <Paper className={classes.paper} elevation={0}>
          <div className={classes.illustration}>
            <CreditCardOffIcon className={classes.illustrationIcon} />
          </div>

          <Typography className={classes.title} component="h2">
            No Active Subscription
          </Typography>

          <Typography className={classes.subtitle} component="p">
            Your company does not have an active subscription plan. Please
            contact your system administrator to assign a new plan.
          </Typography>

          <Box mt={2}>
            <Typography className={classes.helpText}>
              Contact your system administrator
            </Typography>
          </Box>
        </Paper>
      </Container>
    </StyledBox>
  );
};

export default SubscriptionGate;
